/**
 * useAssignable — кого текущий пользователь может назначить (GET /api/team/assignable).
 *
 * Единый источник для всех мест выбора сотрудников (teams_implementation_plan.md,
 * Этап 4): исполнители сметы, «Исполнитель» расхода, ответственный Проекта.
 * Сервер уже отрезал недоступных (Бригадир — только свои команды) и той же
 * функцией проверяет id при сохранении, поэтому список и проверка не расходятся.
 *
 * Раньше эти места читали /settings/team, закрытый team.view: у Бригадира
 * поля молча пропадали (403 → пустой список).
 *
 *   const { users, teams, loaded } = useAssignable(enabled, propertyId);
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client';

const EMPTY = { users: [], teams: [], recent_user_ids: [] };

// На одной странице хук живёт в нескольких местах сразу (раздел Расходов +
// FAB + карточки), а dev-режим React ещё и монтирует дважды — без склейки это
// полдюжины одинаковых запросов разом. Склеиваем одновременные и держим ответ
// недолго: состав команд меняется редко, а устаревание на 15 секунд безвредно
// (сервер всё равно проверяет id при сохранении).
const TTL_MS = 15000;
const cache = new Map(); // key → { at, promise }

export function fetchAssignable(propertyId) {
  const key = String(propertyId || '');
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < TTL_MS) return hit.promise;
  const promise = api.getAssignable(propertyId || null).catch((e) => { cache.delete(key); throw e; });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

/** Сбросить кэш — после правки команд/состава в Настройках. */
export function invalidateAssignable() {
  cache.clear();
}

export default function useAssignable(enabled = true, propertyId = null) {
  const [state, setState] = useState({ ...EMPTY, loaded: false });

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchAssignable(propertyId)
      .then((d) => { if (alive) setState({ ...EMPTY, ...(d || {}), loaded: true }); })
      .catch(() => { if (alive) setState({ ...EMPTY, loaded: true }); });
    return () => { alive = false; };
  }, [enabled, propertyId]);

  return state;
}
